'use client'

import { useEffect, useRef, useState } from 'react'
import { previewPlanCheckout, previewTeamCheckout } from '@/lib/api'
import { PLAN_PRO, PLAN_STUDIO } from '@/lib/constants'
import { resolvePromoCodeHandoff, type PromoCodeHandoff } from '@/lib/promoCodeHandoff'

/**
 * Resolves a handed-off promo code once the account can preview checkouts.
 * `undefined` while unresolved (or when there is no code), so callers can hold
 * the coupon pickers until the right card has its quote.
 */
export function usePromoCodeHandoff(
  code: string | undefined,
  enabled: boolean,
  /** Runs once with the resolved quote so the caller can seed the matching card. */
  onResolved?: (handoff: PromoCodeHandoff) => void,
): PromoCodeHandoff | undefined {
  const [handoff, setHandoff] = useState<PromoCodeHandoff | undefined>(undefined)
  const onResolvedRef = useRef(onResolved)
  useEffect(() => {
    onResolvedRef.current = onResolved
  })

  useEffect(() => {
    if (!code || !enabled) return
    let cancelled = false
    void resolvePromoCodeHandoff(code, {
      pro: value => previewPlanCheckout(PLAN_PRO, value),
      studio: value => previewTeamCheckout(PLAN_STUDIO, value),
    }).then(result => {
      if (cancelled) return
      setHandoff(result)
      onResolvedRef.current?.(result)
    })
    return () => {
      cancelled = true
    }
  }, [code, enabled])

  return handoff
}
